import React, { useState } from 'react';
import { CAlert, CButton, CCollapse } from '@coreui/react';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { formatDateTime } from 'src/constants/supportLetters';
import {
	invalidateCachedRequest, invalidateLettersList,
	invalidateCachedSession, invalidateCachedSupportHistory,
} from 'src/utils/supportRequestCache';

const urlApiRequests = '/support-requests';

/**
 * Позначка про звіт автоімпорту вкладень з пошти.
 * Звіти з `failed > 0` і ще не погашені — жовтий алерт з деталями й кнопкою «Прибрати позначку».
 * Решта (погашені або без помилок) — тихий рядок-фіксація «надійшло з пошти + дата».
 *
 * @param {Array} imports - data.attachment_imports
 * @param {number} requestId - для запиту acknowledge
 * @param {number} [sessionId]
 * @param {string} [userEmail]
 * @param {function} [onAcknowledged] - callback(updatedRequest); якщо не передано — кнопки «Прибрати позначку» немає (read-only)
 */
const SupportAttachmentImportNotice = ({ imports, requestId, sessionId, userEmail, onAcknowledged }) => {
	const [detailsOpenId, setDetailsOpenId] = useState(null);
	const [ackLoading, setAckLoading] = useState(null);

	if (!imports || imports.length === 0) return null;

	const handleAcknowledge = (importId) => {
		confirmAlert({
			title: 'Прибрати позначку про недоливки?',
			message: 'Дію не можна скасувати.',
			buttons: [
				{
					label: 'Так',
					onClick: async () => {
						setAckLoading(importId);
						try {
							const res = await AxiosInstance.post(`${urlApiRequests}/${requestId}/attachment-imports/${importId}/acknowledge`);
							invalidateCachedRequest(requestId);
							invalidateLettersList();
							if (sessionId) invalidateCachedSession(sessionId);
							if (userEmail) invalidateCachedSupportHistory(userEmail);
							toast.success('Позначку прибрано');
							onAcknowledged?.(res.data);
						} catch (err) {
							toast.error(err.response?.data?.error || 'Помилка прибирання позначки');
						} finally {
							setAckLoading(null);
						}
					},
				},
				{ label: 'Ні', onClick: () => { } },
			],
		});
	};

	return (
		<FormSubmitOverlay submitting={ackLoading !== null}>
			<div className="mb-2">
				{imports.map((imp) => {
					const needsAttention = imp.failed > 0 && !imp.acknowledged_at;
					if (!needsAttention) {
						return (
							<div key={imp.id} className="text-muted small mb-1">
								Надійшло з пошти {formatDateTime(imp.imported_at)} ({imp.total} вкладень)
							</div>
						);
					}
					return (
						<CAlert key={imp.id} color="warning" className="py-2">
							<div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
								<div>
									⚠️ Надійшло з пошти {formatDateTime(imp.imported_at)} — додано {imp.succeeded} з {imp.total} вкладень
								</div>
								<div className="d-flex gap-2">
									<CButton
										color="warning"
										variant="outline"
										size="sm"
										onClick={() => setDetailsOpenId((prev) => prev === imp.id ? null : imp.id)}
										title="Показати список недоливків"
									>
										Деталі
									</CButton>
									<CButton
										color="warning"
										size="sm"
										onClick={() => handleAcknowledge(imp.id)}
										disabled={!onAcknowledged}
										title={onAcknowledged ? 'Прибрати позначку' : 'Доступно лише у формі редагування'}
									>
										Прибрати позначку
									</CButton>
								</div>
							</div>
							<CCollapse visible={detailsOpenId === imp.id}>
								<ul className="mb-0 mt-2">
									{(imp.failures || []).map((f, idx) => (
										<li key={idx}>
											<strong>{f.original_name}</strong> — {f.reason}
										</li>
									))}
								</ul>
							</CCollapse>
						</CAlert>
					);
				})}
			</div>
		</FormSubmitOverlay>
	);
};

export default SupportAttachmentImportNotice;
